import { FC, useState, KeyboardEvent } from "react";
import { toast } from "react-toastify";
import VariableChip from "./VariableChip";
import InputFrame from "../../atoms/InputFrame";

type Tag = {
    name: string;
    value: string;
};

type TemplateVariable = {
    name: string;
    format: string;
};

type TagInputProps = {
    label: string;
    value: Tag[];
    onChange: (values: Tag[]) => void;
    errorMessage?: string;
    variables?: TemplateVariable[];
};

const TagInput: FC<TagInputProps> = ({
    label,
    value,
    onChange,
    errorMessage,
    variables = [],
}) => {
    const [name, setName] = useState("");
    const [tagValue, setTagValue] = useState("");

    const addTag = () => {
        if (!name.trim() || !tagValue.trim()) return;
        
        const trimmedName = name.trim();
        const isDuplicate = value.some(tag => tag.name === trimmedName);
        
        if (isDuplicate) {
            toast.error(`Tag com nome "${trimmedName}" já existe`);
            return;
        }
        
        const newTag: Tag = {
            name: trimmedName,
            value: tagValue.trim(),
        };
        
        onChange([...value, newTag]);
        setName("");
        setTagValue("");
    };

    const removeTag = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addTag();
        }
    };

    const isVariable = (text: string) => text.startsWith("{{") && text.endsWith("}}");

    const getVariableFormat = (val: string): string | undefined => {
        if (!isVariable(val)) return undefined;
        const varName = val.replace(/[{}]/g, '');
        const variable = variables.find(v => v.name === varName);
        return variable?.format;
    };

    const error = Boolean(errorMessage);
    const borderColor = error ? "border-red-600 dark:border-red-400" : "border-gray-300 dark:border-gray-600";

    return (
        <InputFrame label={label} errorMessage={errorMessage}>
            <div className="w-full space-y-2">
                <div className="flex gap-2 items-end">
                    <div className="flex-1">
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="nome"
                            className={`block py-2.5 px-0 w-full text-sm bg-transparent border-0 border-b-2 ${borderColor} appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 dark:text-white dark:placeholder-gray-400`}
                        />
                    </div>
                    <div className="flex-1">
                        <input
                            type="text"
                            value={tagValue}
                            onChange={(e) => setTagValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="valor ou {{variavel}}"
                            className={`block py-2.5 px-0 w-full text-sm bg-transparent border-0 border-b-2 ${borderColor} appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 dark:text-white dark:placeholder-gray-400`}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={addTag}
                        className="pb-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        +
                    </button>
                </div>

                {value.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                        {value.map((tag, index) => (
                            <VariableChip
                                key={index}
                                value={tag.value}
                                isVariable={isVariable(tag.value)}
                                onRemove={() => removeTag(index)}
                                format={getVariableFormat(tag.value)}
                                prefix={`${tag.name}:`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </InputFrame>
    );
};

export default TagInput;
